import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { FAQSection } from "@/components/marketing/FAQSection";
import { BLOG_FAQS, faqJsonLd } from "@/content/faqs";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { BlogCard } from "@/components/marketing/BlogCard";
import { BlogRow } from "@/components/marketing/BlogRow";
import { blogIndexTopics, matchesBlogIndexTopic } from "@/lib/blogNavigation";
import type { BlogPostSummary } from "@/lib/blogPosts";
import { abs } from "@/content/siteConfig";

export const Route = createFileRoute("/blog/")({
  loader: async (): Promise<{ posts: BlogPostSummary[]; failed: boolean }> => {
    // Dynamic import keeps the database client out of the shared bundle.
    const { listPublishedPosts } = await import("@/lib/blogPosts");
    const data = await listPublishedPosts(50);
    if (!data) return { posts: [], failed: true };
    return { posts: data, failed: false };
  },
  head: () => ({
    meta: [
      { title: "Blog, Paid Media Strategy for Operators" },
      {
        name: "description",
        content:
          "Operator-facing paid media strategy, Google Ads, Meta Ads, LSA, and YouTube tactics published fresh every other day.",
      },
      { property: "og:title", content: "Blog, Paid media strategy · Berk Hospitality Group" },
      {
        property: "og:description",
        content:
          "Field notes on Google Ads, Meta Ads, LSA, and YouTube for operators spending real money on real accounts.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: abs("/blog") },
      { property: "og:image", content: abs("/og-image.jpg") },
      { name: "twitter:image", content: abs("/og-image.jpg") },
    ],
    links: [{ rel: "canonical", href: abs("/blog") }],
    scripts: [
      { type: "application/ld+json", children: faqJsonLd(BLOG_FAQS) },
    ],
  }),
  component: BlogIndex,
});

function BlogIndex() {
  const [topic, setTopic] = useState("All");
  const { posts, failed } = Route.useLoaderData();
  const topics = blogIndexTopics(posts);

  return (
    <SiteShell>
    <div className="wrap blog-index" style={{ paddingTop: 120, paddingBottom: 120 }}>
      <div style={{ maxWidth: 780 }}>
        <div
          className="mono"
          style={{
            fontSize: 11,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "rgba(0,0,0,0.55)",
          }}
        >
          08 · Blog
        </div>
        <h1
          className="mt-4"
          style={{ fontSize: "clamp(40px, 6vw, 68px)", lineHeight: 1.02, letterSpacing: "-0.02em" }}
        >
          Paid media notes from the desk.
        </h1>
        <p className="mt-6" style={{ fontSize: 18, lineHeight: 1.55, color: "var(--color-ink)" }}>
          Field notes on Google Ads, Meta, Local Services Ads and conversion tracking, for owners and
          marketing leads who run their own paid media. Every post comes out of real account work.
        </p>
        <p className="mt-5" style={{ fontSize: 16, lineHeight: 1.6, color: "var(--color-ink)" }}>
          Rather have someone look at your accounts? <Link to="/audit" className="underline underline-offset-4">Start with a free audit.</Link>
        </p>
      </div>

      <div className="blog-topic-chips mt-8" role="group" aria-label="Filter posts by topic">
        {topics.map((label) => (
            <Button key={label} variant="outline" className="blog-topic-chip mono" aria-pressed={topic === label} onClick={() => setTopic(label)}>
              {label} <span>{posts.filter((post) => matchesBlogIndexTopic(post.category, label)).length}</span>
            </Button>
        ))}
      </div>
      <div className="mt-6" aria-label="Blog posts">
        {failed && (
          <div style={{ color: "rgba(0,0,0,0.6)" }}>Unable to load posts right now.</div>
        )}
        {!failed && posts.length === 0 && (
          <div style={{ color: "rgba(0,0,0,0.6)" }}>
            The first post drops with the next scheduled run.
          </div>
        )}
        <div className="blog-featured grid gap-4 md:grid-cols-3">
          {posts.slice(0, 3).map((post) => <BlogCard key={post.slug} post={post} hidden={!matchesBlogIndexTopic(post.category, topic)} />)}
        </div>
        <div className="blog-post-list mt-6">
          {posts.slice(3).map((post) => <BlogRow key={post.slug} post={post} hidden={!matchesBlogIndexTopic(post.category, topic)} />)}
        </div>
      </div>

      <p className="mt-12" style={{ fontSize: 16, lineHeight: 1.6, color: "rgba(0,0,0,0.68)" }}>
        Original data: see our{" "}
        <Link to="/research" className="underline underline-offset-4">Research</Link>.
      </p>
    </div>
      <FAQSection items={BLOG_FAQS} />
    </SiteShell>
  );
}
